import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { AlertCircle, Calendar, Car, Check, CheckCircle, Clock, Eye, FileText, Filter, Fuel, Home, Play, Plus, RefreshCw, Square, Trash2, Upload, Users } from "lucide-react";
import { Badge, Button, Card, Input, Modal, Select, Spinner, Textarea } from "../shared/ui";
import { useAuth } from "../App";
import PDFDocumentSigner from "../components/PDFDocumentSigner";
import { uploadReceipt } from "../lib/uploadReceipt";
import { WorkingAwayCard } from "../components/timesheets/WorkingAwayCard";
import { OnShiftReceiptCard } from "../components/timesheets/OnShiftReceiptCard";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TimePage = () => {
  const { user } = useAuth();
  const [clockStatus, setClockStatus] = useState(null);
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState("");
  
  // Signing documents state
  const [pendingSigningDocs, setPendingSigningDocs] = useState([]);
  const [showSigningModal, setShowSigningModal] = useState(false);
  const [currentSigningDoc, setCurrentSigningDoc] = useState(null);
  const [signingSignature, setSigningSignature] = useState('');

  // Missed clock-out prompt state
  const [showMissedClockOutModal, setShowMissedClockOutModal] = useState(false);
  const [missedClockOutInfo, setMissedClockOutInfo] = useState(null);
  const [missedClockOutTime, setMissedClockOutTime] = useState('');
  const [missedClockOutSaving, setMissedClockOutSaving] = useState(false);
  
  // Dogman/Operator linking state
  const [availableDogmen, setAvailableDogmen] = useState([]);
  const [selectedDogman, setSelectedDogman] = useState("");
  const [pendingDogmanLinks, setPendingDogmanLinks] = useState([]);
  const [activeDogmanLinks, setActiveDogmanLinks] = useState({ as_operator: [], as_dogman: [] });
  const [showDogmanLinkModal, setShowDogmanLinkModal] = useState(false);
  const [selectedMachine, setSelectedMachine] = useState("");
  const [machineReadings, setMachineReadings] = useState(null);
  const [loadingReadings, setLoadingReadings] = useState(false);
  
  // Admin/Manager reports
  const [activeTab, setActiveTab] = useState("my-time");
  const [reportPeriod, setReportPeriod] = useState("4weeks");
  const [workerReports, setWorkerReports] = useState(null);
  const [allEntries, setAllEntries] = useState([]);
  
  // Travel tracking state
  const [travelStatus, setTravelStatus] = useState(null);
  const [travelEntries, setTravelEntries] = useState([]);
  const [machines, setMachines] = useState([]);
  const [activeDrivers, setActiveDrivers] = useState([]);
  const [showTravelClockIn, setShowTravelClockIn] = useState(false);
  const [showFuelStop, setShowFuelStop] = useState(false);
  const [travelRole, setTravelRole] = useState("driver");
  const [travelClockInData, setTravelClockInData] = useState({
    starting_point: "",
    destination: "",
    vehicle_id: "",
    vehicle_name: "",
    vehicle_rego: "",
    starting_km: "",
    pre_start_checked: false,
    machine_faults: "",
    join_travel_id: "",
    driver_name: "",
    notes: ""
  });
  const [travelClockOutData, setTravelClockOutData] = useState({
    finishing_point: "",
    ending_km: "",
    incidents: "",
    notes: ""
  });
  const [fuelStopData, setFuelStopData] = useState({
    km_at_stop: "",
    station_location: "",
    receipt_url: null,
    fuel_amount: "",
    fuel_cost: "",
    payment_type: "company",
    notes: ""
  });

  // Enhanced Clock Out state
  const [showEnhancedClockOut, setShowEnhancedClockOut] = useState(false);
  const [includeDogmanClockOut, setIncludeDogmanClockOut] = useState(true);
  const [supervisors, setSupervisors] = useState([]);
  const [clockOutData, setClockOutData] = useState({
    supervisor_id: "",
    work_description: "",
    is_last_shift_of_week: false,
    reimbursements: []
  });
  const [newReimbursement, setNewReimbursement] = useState({
    description: "",
    amount: "",
    receipt_url: null
  });

  // End of Week state
  const [showEndOfWeek, setShowEndOfWeek] = useState(false);
  const [weekSummary, setWeekSummary] = useState(null);
  const [adjustedEntries, setAdjustedEntries] = useState([]);
  
  const canViewReports = ['admin', 'manager', 'office_staff'].includes(user?.role);
  const isAdmin = user?.role === 'admin' || user?.role === 'manager';

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (canViewReports && activeTab === "reports") {
      fetchWorkerReports();
    }
    if (canViewReports && activeTab === "all-entries") {
      fetchAllEntries();
    }
  }, [activeTab, reportPeriod]);

  const fetchData = async () => {
    try {
      const [statusRes, entriesRes, summaryRes, jobsRes, travelStatusRes, machinesRes, driversRes] = await Promise.all([
        axios.get(`${API}/time/status`),
        axios.get(`${API}/time/entries`),
        axios.get(`${API}/time/summary`),
        axios.get(`${API}/jobs`),
        axios.get(`${API}/travel/status`),
        axios.get(`${API}/machines`),
        axios.get(`${API}/travel/active-drivers`)
      ]);
      setClockStatus(statusRes.data);
      setEntries(entriesRes.data);
      setSummary(summaryRes.data);
      
      // Filter jobs to show ALL jobs workers might need (excluding completed/cancelled)
      const activeJobs = (jobsRes.data || []).filter(j => 
        j.status !== 'completed' && j.status !== 'cancelled'
      );
      setJobs(activeJobs);
      
      setTravelStatus(travelStatusRes.data);
      setMachines(machinesRes.data);
      setActiveDrivers(driversRes.data);
      
      // Fetch dogman link data
      fetchDogmanData();
      
      // Fetch supervisors for clock-out
      const supervisorsRes = await axios.get(`${API}/users`);
      const supervisorList = supervisorsRes.data.filter(u => 
        ['admin', 'manager', 'supervisor'].includes(u.role)
      );
      setSupervisors(supervisorList);
    } catch (e) {
      console.error("Error fetching time page data:", e);
      // Try to at least load jobs separately if the main fetch fails
      try {
        const jobsRes = await axios.get(`${API}/jobs`);
        const activeJobs = (jobsRes.data || []).filter(j => 
          j.status !== 'completed' && j.status !== 'cancelled'
        );
        setJobs(activeJobs);
      } catch (jobErr) {
        console.error("Failed to load jobs:", jobErr);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchTravelEntries = async () => {
    try {
      const res = await axios.get(`${API}/travel/entries?days=30`);
      setTravelEntries(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchMachineReadings = async (machineId) => {
    if (!machineId) { setMachineReadings(null); return; }
    setLoadingReadings(true);
    try {
      const res = await axios.get(`${API}/machines/${machineId}/last-readings`);
      setMachineReadings(res.data);
    } catch (e) {
      setMachineReadings(null);
    } finally {
      setLoadingReadings(false);
    }
  };


  const fetchDogmanData = async () => {
    try {
      // Get pending link requests (as dogman)
      const pendingRes = await axios.get(`${API}/dogman-links/pending`);
      setPendingDogmanLinks(pendingRes.data);
      
      // Get active links
      const activeRes = await axios.get(`${API}/dogman-links/active`);
      setActiveDogmanLinks(activeRes.data);
      
      // Get available dogmen for linking (for operators)
      if (user?.role === 'crane_operator' || user?.role === 'admin' || user?.role === 'manager') {
        const dogmenRes = await axios.get(`${API}/users/dogmen`);
        setAvailableDogmen(dogmenRes.data);
      }
    } catch (e) {
      console.error('Dogman data fetch error:', e);
    }
  };

  const handleAcceptDogmanLink = async (linkId) => {
    try {
      await axios.post(`${API}/dogman-links/${linkId}/accept`);
      toast.success("Link accepted! You are now clocked in.");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to accept link");
    }
  };

  const handleDeclineDogmanLink = async (linkId) => {
    try {
      await axios.post(`${API}/dogman-links/${linkId}/decline`);
      toast.success("Link declined");
      fetchDogmanData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to decline link");
    }
  };

  const handleBulkClockOut = async (includeDogtman = true) => {
    try {
      const res = await axios.post(`${API}/time/bulk-clock-out`, {
        include_linked_dogman: includeDogtman,
        clock_out_notes: clockOutNotes,
        work_completed: workCompleted,
        next_day_plan: nextDayPlan
      });
      toast.success(res.data.message + (res.data.dogman_clocked_out ? ` (Including ${res.data.dogman_clocked_out})` : ''));
      setShowEnhancedClockOut(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to clock out");
    }
  };

  const fetchWorkerReports = async () => {
    try {
      const res = await axios.get(`${API}/time/reports/workers?period=${reportPeriod}`);
      setWorkerReports(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchAllEntries = async () => {
    try {
      const res = await axios.get(`${API}/time/entries`);
      setAllEntries(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleCancelEntry = async (entryId) => {
    if (!window.confirm("Are you sure you want to cancel this time entry? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/time/entries/${entryId}`);
      alert("Time entry cancelled");
      fetchData();
      if (activeTab === "all-entries") fetchAllEntries();
      if (activeTab === "reports") fetchWorkerReports();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to cancel entry");
    }
  };

  const handleForceClockOutEntry = async (entryId) => {
    const clockOutTime = window.prompt("Enter clock out time (leave blank for now):", "");
    try {
      await axios.post(`${API}/time/force-clock-out-by-entry/${entryId}`, null, {
        params: clockOutTime ? { clock_out_time: new Date(clockOutTime).toISOString() } : {}
      });
      alert("Entry clocked out");
      fetchData();
      if (activeTab === "all-entries") fetchAllEntries();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to clock out");
    }
  };

  const handleCheckOverdue = async () => {
    try {
      const res = await axios.get(`${API}/time/check-overdue`);
      if (res.data.clocked_out.length > 0) {
        alert(`Auto clocked out ${res.data.clocked_out.length} workers who were clocked in for over 24 hours.`);
      } else {
        alert("No overdue clock-ins found.");
      }
      fetchData();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to check overdue");
    }
  };

  const handleClockAction = async (action) => {
    // Job selection is mandatory for clock-in
    if (action === 'clock-in' && !selectedJob) {
      alert("Please select a job before clocking in");
      return;
    }

    // Missed clock-out detection: if user has an open entry from a prior day, prompt to correct
    if (action === 'clock-in') {
      try {
        const openRes = await axios.get(`${API}/time/open-entry`);
        if (openRes.data?.has_open_entry && openRes.data?.is_from_prior_day) {
          setMissedClockOutInfo(openRes.data);
          // Default the corrected clock-out to a reasonable end-of-day of the clock-in date
          const inDate = new Date(openRes.data.clock_in_iso);
          const defaultOut = new Date(inDate);
          defaultOut.setHours(17, 0, 0, 0); // 5pm local on the clock-in day
          setMissedClockOutTime(defaultOut.toISOString().slice(0, 16));
          setShowMissedClockOutModal(true);
          return; // Block until corrected
        }
      } catch (e) {
        console.error('Failed to check open entry:', e);
        // Non-fatal — continue
      }
    }
    
    // Check for pending signing documents on clock-in
    if (action === 'clock-in' && selectedJob) {
      try {
        const pendingRes = await axios.get(`${API}/clock/pending-documents?job_id=${selectedJob}`);
        if (pendingRes.data.has_pending) {
          setPendingSigningDocs(pendingRes.data.documents);
          setShowSigningModal(true);
          return; // Don't proceed until documents are signed
        }
      } catch (e) {
        console.error('Failed to check pending documents:', e);
        // Continue with clock-in even if check fails
      }
    }
    
    await proceedWithClockIn(action);
  };
  
  const proceedWithClockIn = async (action) => {
    // Check if job requires GPS
    const job = jobs.find(j => j.id === selectedJob);
    const needsGPS = job?.gps_required;
    
    if (needsGPS) {
      // Get current location
      if (!navigator.geolocation) {
        alert("Your browser doesn't support GPS location. Please use a different browser or device.");
        return;
      }
      
      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0
          });
        });
        
        await axios.post(`${API}/time/${action}`, { 
          job_id: selectedJob || null,
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          linked_dogman_id: selectedDogman || null,
          machine_id: selectedMachine || null
        });
        setSelectedDogman("");
        setSelectedMachine("");
        fetchData();
      } catch (geoError) {
        if (geoError.code === 1) {
          alert("Location access denied. This job requires GPS to clock in. Please enable location services and try again.");
        } else if (geoError.code === 2) {
          alert("Unable to get your location. Please check your GPS settings and try again.");
        } else if (geoError.code === 3) {
          alert("Location request timed out. Please try again.");
        } else {
          alert(geoError.response?.data?.detail || `${action} failed: ${geoError.message}`);
        }
      }
    } else {
      try {
        await axios.post(`${API}/time/${action}`, { 
          job_id: selectedJob || null,
          linked_dogman_id: selectedDogman || null,
          machine_id: selectedMachine || null
        });
        setSelectedDogman("");
        setSelectedMachine("");
        fetchData();
      } catch (e) {
        alert(e.response?.data?.detail || `${action} failed`);
      }
    }
  };

  // ==================== TRAVEL HANDLERS ====================
  
  const handleTravelClockIn = async (e) => {
    e.preventDefault();
    if (!travelClockInData.starting_point || !travelClockInData.destination) {
      alert("Please enter starting point and destination");
      return;
    }
    // Only drivers need starting KM
    if (travelRole === "driver" && !travelClockInData.starting_km) {
      alert("Please enter the starting KM from the odometer");
      return;
    }
    // Passengers must select a driver to join OR enter driver name
    if (travelRole === "passenger" && !travelClockInData.join_travel_id && !travelClockInData.driver_name) {
      alert("Please select a driver to join or enter the driver's name");
      return;
    }
    try {
      const data = {
        ...travelClockInData,
        travel_role: travelRole,
        starting_km: travelRole === "driver" ? parseFloat(travelClockInData.starting_km) : null
      };
      await axios.post(`${API}/travel/clock-in`, data);
      alert(`Travel clock in successful as ${travelRole}!`);
      setShowTravelClockIn(false);
      setTravelRole("driver");
      setTravelClockInData({
        starting_point: "",
        destination: "",
        vehicle_id: "",
        vehicle_name: "",
        vehicle_rego: "",
        starting_km: "",
        pre_start_checked: false,
        machine_faults: "",
        join_travel_id: "",
        driver_name: "",
        notes: ""
      });
      fetchData();
    } catch (e) {
      alert(e.response?.data?.detail || "Travel clock in failed");
    }
  };

  const handleTravelClockOut = async (e) => {
    e.preventDefault();
    if (!travelClockOutData.finishing_point) {
      alert("Please enter the finishing point");
      return;
    }
    // Only drivers need ending KM
    const isDriver = travelStatus?.entry?.travel_role === "driver";
    if (isDriver && !travelClockOutData.ending_km) {
      alert("Please enter the ending KM");
      return;
    }
    try {
      const data = {
        ...travelClockOutData,
        ending_km: isDriver && travelClockOutData.ending_km ? parseFloat(travelClockOutData.ending_km) : null
      };
      await axios.post(`${API}/travel/clock-out`, data);
      alert("Travel clock out successful!");
      setTravelClockOutData({
        finishing_point: "",
        ending_km: "",
        incidents: "",
        notes: ""
      });
      fetchData();
    } catch (e) {
      alert(e.response?.data?.detail || "Travel clock out failed");
    }
  };

  const handleFuelStop = async (e) => {
    e.preventDefault();
    if (!fuelStopData.km_at_stop || !fuelStopData.station_location) {
      alert("Please enter KM and station location");
      return;
    }
    try {
      const data = {
        ...fuelStopData,
        km_at_stop: parseFloat(fuelStopData.km_at_stop),
        fuel_amount: fuelStopData.fuel_amount ? parseFloat(fuelStopData.fuel_amount) : null,
        fuel_cost: fuelStopData.fuel_cost ? parseFloat(fuelStopData.fuel_cost) : null
      };
      await axios.post(`${API}/travel/fuel-stop`, data);
      alert("Fuel stop recorded!");
      setShowFuelStop(false);
      setFuelStopData({
        km_at_stop: "",
        station_location: "",
        receipt_url: null,
        fuel_amount: "",
        fuel_cost: "",
        payment_type: "company",
        notes: ""
      });
      fetchData();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to record fuel stop");
    }
  };

  const handleReceiptUpload = async (file) => {
    if (!file) return;
    try {
      toast.info("Uploading receipt...");
      const r = await uploadReceipt(file);
      setFuelStopData({ ...fuelStopData, receipt_url: r.url });
      toast.success("Receipt uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Receipt upload failed");
    }
  };

  // ==================== ENHANCED CLOCK OUT HANDLERS ====================

  const handleEnhancedClockOut = async (e) => {
    e.preventDefault();
    if (!clockOutData.supervisor_id) {
      alert("Please select your supervisor");
      return;
    }
    if (!clockOutData.work_description) {
      alert("Please enter a description of today's work");
      return;
    }
    try {
      const payload = {
        ...clockOutData,
        include_linked_dogman: includeDogmanClockOut && activeDogmanLinks.as_operator?.some(l => l.dogman_still_clocked_in)
      };
      const res = await axios.post(`${API}/time/clock-out-enhanced`, payload);
      const dogmanMsg = res.data.dogman_clocked_out ? `\nAlso clocked out: ${res.data.dogman_clocked_out}` : '';
      const autoMsg = res.data.auto_submitted ? '\nTimesheet auto-submitted for approval!' : '';
      alert(`Clocked out successfully!\nHours: ${res.data.total_hours}h\nSupervisor: ${res.data.supervisor}${dogmanMsg}${autoMsg}`);
      setShowEnhancedClockOut(false);
      setIncludeDogmanClockOut(true);
      setClockOutData({
        supervisor_id: "",
        work_description: "",
        is_last_shift_of_week: false,
        reimbursements: []
      });
      fetchData();
      fetchDogmanData();
      
      // If last shift of week, prompt to open end of week modal
      if (res.data.is_last_shift_of_week) {
        if (window.confirm("Ready to submit your timesheet for the week?")) {
          fetchWeekSummary();
          setShowEndOfWeek(true);
        }
      }
    } catch (e) {
      alert(e.response?.data?.detail || "Clock out failed");
    }
  };

  const addReimbursement = () => {
    if (!newReimbursement.description || !newReimbursement.amount) {
      alert("Please enter description and amount for the reimbursement");
      return;
    }
    setClockOutData({
      ...clockOutData,
      reimbursements: [...clockOutData.reimbursements, {
        ...newReimbursement,
        amount: parseFloat(newReimbursement.amount)
      }]
    });
    setNewReimbursement({ description: "", amount: "", receipt_url: null });
  };

  const handleReimbursementReceiptUpload = async (file) => {
    if (!file) return;
    try {
      toast.info("Uploading receipt...");
      const r = await uploadReceipt(file);
      setNewReimbursement({ ...newReimbursement, receipt_url: r.url });
      toast.success("Receipt uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Receipt upload failed");
    }
  };

  const fetchWeekSummary = async () => {
    try {
      const res = await axios.get(`${API}/time/current-week-summary`);
      setWeekSummary(res.data);
      setAdjustedEntries(res.data.entries.map(e => ({...e})));
    } catch (e) {
      console.error(e);
    }
  };

  const handleEndOfWeekSubmit = async () => {
    if (!weekSummary) return;
    
    const supervisorId = weekSummary.timesheet?.supervisor_id || clockOutData.supervisor_id;
    if (!supervisorId) {
      alert("Please select a supervisor for approval");
      return;
    }
    
    try {
      await axios.post(`${API}/time/end-of-week`, {
        week_start: weekSummary.week_start,
        supervisor_id: supervisorId,
        adjusted_entries: adjustedEntries,
        notes: ""
      });
      alert("Timesheet submitted to supervisor for approval!");
      setShowEndOfWeek(false);
      fetchData();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to submit timesheet");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in pb-32 sm:pb-6" data-testid="time-page">
      {/* MOBILE sticky action buttons — always reachable above the bottom nav */}
      {activeTab === 'my-time' && clockStatus?.clocked_in && (
        <div className="sm:hidden fixed bottom-20 left-0 right-0 z-50 p-3 bg-white border-t-2 border-red-300 shadow-2xl" data-testid="mobile-clockout-sticky">
          <Button
            variant="destructive"
            onClick={() => { fetchDogmanData(); setShowEnhancedClockOut(true); }}
            className="w-full text-xl py-5 rounded-xl font-bold touch-manipulation"
            style={{ minHeight: '64px' }}
            data-testid="mobile-clockout-btn"
          >
            <Square className="w-6 h-6 mr-2" /> CLOCK OUT
          </Button>
        </div>
      )}
      {activeTab === 'travel' && travelStatus?.travelling && travelStatus?.entry?.travel_role === 'driver' && (
        <div className="sm:hidden fixed bottom-[calc(5rem+88px)] left-2 right-2 z-50" data-testid="mobile-fuel-sticky">
          <Button
            variant="outline"
            onClick={() => setShowFuelStop(true)}
            className="w-full py-4 rounded-xl font-bold touch-manipulation bg-white shadow-xl border-2 border-amber-400"
            data-testid="mobile-fuel-btn"
          >
            <Fuel className="w-5 h-5 mr-2 text-amber-600" /> Fuel Stop
          </Button>
        </div>
      )}
      {/* Pending Dogman Link Requests Banner */}
      {pendingDogmanLinks.length > 0 && (
        <Card className="bg-blue-50 border-2 border-blue-300">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-blue-900">Operator Link Request</h3>
              {pendingDogmanLinks.map(link => (
                <div key={link.id} className="mt-2 p-3 bg-white rounded-lg border">
                  <p className="font-medium">{link.operator_name} wants to link you as their dogman</p>
                  <p className="text-sm text-slate-600">Job: {link.job_title || 'Unknown'}</p>
                  {link.machine_name && <p className="text-sm text-slate-600">Machine: {link.machine_name}</p>}
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" onClick={() => handleAcceptDogmanLink(link.id)} className="flex-1">
                      Accept & Clock In
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleDeclineDogmanLink(link.id)}>
                      Decline
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold uppercase tracking-tight font-heading">Time Tracking</h1>
        {canViewReports && (
          <Button variant="outline" onClick={handleCheckOverdue}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Check 24hr Overdue
          </Button>
        )}
      </div>

      {/* Tabs for Admin/Manager */}
      {canViewReports && (
        <div className="flex gap-2 border-b-2 border-slate-200 pb-4 flex-wrap">
          {['my-time', 'travel', 'all-entries', 'reports'].map(tab => (
            <button
              key={tab}
              onClick={() => {
                setActiveTab(tab);
                if (tab === 'travel') fetchTravelEntries();
              }}
              className={`px-4 py-2 rounded-md font-semibold text-sm uppercase transition-colors ${
                activeTab === tab ? 'bg-orange-500 text-slate-900' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {tab === 'my-time' ? 'My Time' : tab === 'travel' ? 'Travel' : tab === 'all-entries' ? 'All Entries' : 'Worker Reports'}
            </button>
          ))}
        </div>
      )}

      {/* Travel Tab for non-admins */}
      {!canViewReports && (
        <div className="flex gap-2 border-b-2 border-slate-200 pb-4">
          {['my-time', 'travel'].map(tab => (
            <button
              key={tab}
              onClick={() => {
                setActiveTab(tab);
                if (tab === 'travel') fetchTravelEntries();
              }}
              className={`px-4 py-2 rounded-md font-semibold text-sm uppercase transition-colors ${
                activeTab === tab ? 'bg-orange-500 text-slate-900' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {tab === 'my-time' ? 'My Time' : 'Travel'}
            </button>
          ))}
        </div>
      )}

      {/* My Time Tab */}
      {activeTab === 'my-time' && (
        <div className="pb-40 sm:pb-6">
          {/* Clock In/Out Card - Top of page, always visible, no scrolling needed */}
          <div className="relative z-10 mb-4">
            <Card className="text-center bg-white shadow-lg border-2 border-orange-500 rounded-xl">
              <div className="p-4 md:p-6">
                {/* Status Badge - Compact on mobile */}
                <div className="mb-3 md:mb-4">
                  <Badge variant={clockStatus?.clocked_in ? "success" : "default"} className="text-base md:text-lg px-4 md:px-6 py-1.5 md:py-2">
                    {clockStatus?.clocked_in ? "CLOCKED IN" : "CLOCKED OUT"}
                  </Badge>
                  {clockStatus?.current_entry && (
                    <div className="mt-2 text-xs md:text-sm text-slate-500">
                      <span className="text-orange-600 font-semibold">
                        {jobs.find(j => j.id === clockStatus.current_entry.job_id)?.title || 'Unknown Job'}
                      </span>
                      <span className="mx-2">•</span>
                      <span>Since {new Date(clockStatus.current_entry.clock_in).toLocaleTimeString('en-AU', {hour: '2-digit', minute: '2-digit'})}</span>
                    </div>
                  )}
                </div>

                {/* Job Selection - Only show when NOT clocked in */}
                {!clockStatus?.clocked_in && (
                  <div className="mb-3 md:mb-4">
                    <select
                      value={selectedJob}
                      onChange={(e) => setSelectedJob(e.target.value)}
                      className="w-full px-4 py-3 md:py-3 text-base md:text-lg font-medium border-2 border-slate-300 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-200 bg-white"
                      style={{ fontSize: '16px' }} /* Prevents iOS zoom on focus */
                    >
                      <option value="">-- Select a Job --</option>
                      {jobs.map(j => (
                        <option key={j.id} value={j.id}>
                          {j.gps_required ? `📍 ${j.title}` : j.title}
                        </option>
                      ))}
                    </select>
                    {!selectedJob && (
                      <p className="text-xs md:text-sm text-amber-600 mt-1.5 font-medium">
                        Select a job to clock in
                      </p>
                    )}
                  </div>
                )}

                {/* Dogman Selection - For crane operators */}
                {!clockStatus?.clocked_in && selectedJob && (user?.role === 'crane_operator' || user?.role === 'admin') && availableDogmen.length > 0 && (
                  <div className="mb-3 md:mb-4 border-t border-slate-200 pt-3">
                    <p className="text-xs text-slate-500 uppercase font-bold mb-2">Link a Dogman (optional)</p>
                    <select
                      value={selectedDogman}
                      onChange={(e) => setSelectedDogman(e.target.value)}
                      className="w-full px-4 py-2 text-sm font-medium border-2 border-slate-200 rounded-lg focus:border-blue-500 bg-white"
                      style={{ fontSize: '16px' }}
                    >
                      <option value="">-- No Dogman --</option>
                      {availableDogmen.filter(d => d.is_available).map(d => (
                        <option key={d.id} value={d.id}>
                          {d.first_name} {d.last_name} ({d.role?.replace('_', ' ')})
                        </option>
                      ))}
                    </select>
                    {selectedDogman && (
                      <p className="text-xs text-blue-600 mt-1">
                        They'll be notified and can accept to be clocked in with you
                      </p>
                    )}
                  </div>
                )}

                {/* Machine Selection + KM/Hours Pre-load */}
                {!clockStatus?.clocked_in && selectedJob && machines.length > 0 && (
                  <div className="mb-3 md:mb-4 border-t border-slate-200 pt-3">
                    <p className="text-xs text-slate-500 uppercase font-bold mb-2">Select Machine (optional)</p>
                    <select
                      value={selectedMachine}
                      onChange={(e) => { setSelectedMachine(e.target.value); fetchMachineReadings(e.target.value); }}
                      className="w-full px-4 py-2 text-sm font-medium border-2 border-slate-200 rounded-lg focus:border-orange-500 bg-white"
                      style={{ fontSize: '16px' }}
                      data-testid="machine-select"
                    >
                      <option value="">-- No Machine --</option>
                      {machines.map(m => (
                        <option key={m.id} value={m.id}>{m.name} {m.rego ? `(${m.rego})` : ''}</option>
                      ))}
                    </select>
                    {selectedMachine && machineReadings && (
                      <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm" data-testid="machine-readings">
                        <p className="font-bold text-amber-800 text-xs uppercase mb-1">Last Recorded Readings</p>
                        <div className="flex gap-4">
                          {machineReadings.last_hours != null && (
                            <div><span className="text-slate-500">Hours:</span> <span className="font-bold">{machineReadings.last_hours}</span></div>
                          )}
                          {machineReadings.last_kms != null && (
                            <div><span className="text-slate-500">KM:</span> <span className="font-bold">{machineReadings.last_kms}</span></div>
                          )}
                        </div>
                        {machineReadings.last_recorded_by && (
                          <p className="text-xs text-slate-400 mt-1">Recorded by {machineReadings.last_recorded_by}</p>
                        )}
                        {!machineReadings.last_hours && !machineReadings.last_kms && (
                          <p className="text-xs text-slate-400">No previous readings recorded</p>
                        )}
                      </div>
                    )}
                    {selectedMachine && loadingReadings && (
                      <p className="text-xs text-slate-400 mt-1">Loading readings...</p>
                    )}
                  </div>
                )}

                {/* Clock In/Out Buttons - Extra large and easy to tap */}
                <div className="flex flex-col gap-2 md:gap-3">
                  {clockStatus?.clocked_in ? (
                    <Button 
                      variant="destructive" 
                      onClick={() => { fetchDogmanData(); setShowEnhancedClockOut(true); }}
                      data-testid="clock-out-btn"
                      className="w-full text-xl md:text-lg py-5 md:py-4 px-8 rounded-xl font-bold tracking-wide touch-manipulation"
                      style={{ minHeight: '60px' }}
                    >
                      <Square className="w-6 h-6 mr-2" />
                      CLOCK OUT
                    </Button>
                  ) : (
                    <Button 
                      onClick={() => handleClockAction('clock-in')}
                      disabled={!selectedJob}
                      data-testid="clock-in-btn"
                      className={`w-full text-xl md:text-lg py-5 md:py-4 px-8 rounded-xl font-bold tracking-wide touch-manipulation ${!selectedJob ? "opacity-50" : ""}`}
                      style={{ minHeight: '60px' }}
                    >
                      <Play className="w-6 h-6 mr-2" />
                      CLOCK IN
                    </Button>
                  )}
                  
                  <Button 
                    variant="outline" 
                    onClick={() => { fetchWeekSummary(); setShowEndOfWeek(true); }}
                    data-testid="end-of-week-btn"
                    className="w-full py-3 rounded-xl touch-manipulation"
                  >
                    <Calendar className="w-5 h-5 mr-2" />
                    Submit Week
                  </Button>
                </div>
              </div>
            </Card>
          </div>

          {/* Time entries and history below */}

      {/* Enhanced Clock Out Modal */}
      {showEnhancedClockOut && (
        <Modal isOpen={showEnhancedClockOut} onClose={() => setShowEnhancedClockOut(false)} title="Clock Out - End of Shift">
          <form onSubmit={handleEnhancedClockOut} className="space-y-6">
            {/* Supervisor Selection */}
            <Select
              label="Who was your Supervisor today? *"
              value={clockOutData.supervisor_id}
              onChange={(e) => setClockOutData({...clockOutData, supervisor_id: e.target.value})}
              options={[
                { value: "", label: "-- Select Supervisor --" },
                ...supervisors.map(s => ({
                  value: s.id,
                  label: `${s.first_name} ${s.last_name} (${s.role})`
                }))
              ]}
              required
            />

            {/* Work Description */}
            <Textarea
              label="Description of Today's Work *"
              value={clockOutData.work_description}
              onChange={(e) => setClockOutData({...clockOutData, work_description: e.target.value})}
              placeholder="What did you do today? (e.g., Rigging on main lift, crane setup, etc.)"
              required
            />

            {/* Receipts/Reimbursements Section */}
            <div className="border-t pt-4">
              <h5 className="text-sm font-bold uppercase text-slate-600 mb-3">Receipts / Reimbursements</h5>
              
              {/* Existing reimbursements */}
              {clockOutData.reimbursements.length > 0 && (
                <div className="space-y-2 mb-4">
                  {clockOutData.reimbursements.map((r, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                      <div>
                        <p className="font-semibold">{r.description}</p>
                        <p className="text-sm text-green-600">${r.amount.toFixed(2)}</p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setClockOutData({
                          ...clockOutData,
                          reimbursements: clockOutData.reimbursements.filter((_, i) => i !== idx)
                        })}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add new reimbursement */}
              <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                <div className="grid md:grid-cols-2 gap-3">
                  <Input
                    label="Description"
                    value={newReimbursement.description}
                    onChange={(e) => setNewReimbursement({...newReimbursement, description: e.target.value})}
                    placeholder="e.g., Fuel, Lunch, Supplies"
                  />
                  <Input
                    label="Amount ($)"
                    type="number"
                    step="0.01"
                    value={newReimbursement.amount}
                    onChange={(e) => setNewReimbursement({...newReimbursement, amount: e.target.value})}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold uppercase text-slate-600 mb-2">
                    Upload Receipt
                  </label>
                  {newReimbursement.receipt_url ? (
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-sm text-green-700">Receipt uploaded</span>
                      <Button type="button" variant="ghost" size="sm" onClick={() => setNewReimbursement({...newReimbursement, receipt_url: null})}>
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleReimbursementReceiptUpload(e.target.files[0])}
                      className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-700"
                    />
                  )}
                </div>
                <Button type="button" variant="outline" onClick={addReimbursement}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Reimbursement
                </Button>
              </div>
            </div>

            {/* Linked Dogman Clock Out */}
            {activeDogmanLinks.as_operator?.some(l => l.dogman_still_clocked_in) && (
              <div className="border-t pt-4">
                <div className="p-4 bg-blue-50 rounded-lg border-2 border-blue-300">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="includeDogman"
                      checked={includeDogmanClockOut}
                      onChange={(e) => setIncludeDogmanClockOut(e.target.checked)}
                      className="w-5 h-5 mt-0.5 rounded border-2 border-blue-400 text-blue-500 focus:ring-blue-500"
                      data-testid="include-dogman-clockout"
                    />
                    <div className="flex-1">
                      <label htmlFor="includeDogman" className="font-bold text-blue-900 cursor-pointer">
                        Also clock out linked Dogman
                      </label>
                      <div className="mt-2 space-y-1">
                        {activeDogmanLinks.as_operator.filter(l => l.dogman_still_clocked_in).map(link => (
                          <div key={link.id} className="flex items-center gap-2 text-sm text-blue-800">
                            <Users className="w-4 h-4 flex-shrink-0" />
                            <span className="font-medium">{link.dogman_name}</span>
                            <span className="text-blue-500">({link.dogman_role?.replace('_', ' ')})</span>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-blue-600 mt-1">
                        {includeDogmanClockOut 
                          ? "They'll be clocked out at the same time as you" 
                          : "They'll need to clock out separately"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* End of Week Question */}
            <div className="border-t pt-4">
              <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
                <input
                  type="checkbox"
                  id="isLastShift"
                  checked={clockOutData.is_last_shift_of_week}
                  onChange={(e) => setClockOutData({...clockOutData, is_last_shift_of_week: e.target.checked})}
                  className="w-5 h-5 rounded border-2 border-blue-400 text-blue-500 focus:ring-blue-500"
                />
                <label htmlFor="isLastShift" className="font-semibold text-blue-800">
                  This is my last shift for the week
                </label>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                If checked, you'll be prompted to review and submit your timesheet after clocking out.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t sticky bottom-0 bg-white pb-2">
              <Button 
                type="submit" 
                variant="destructive" 
                data-testid="confirm-clock-out"
                className="w-full sm:w-auto text-lg py-4 px-8 rounded-xl font-bold touch-manipulation"
                style={{ minHeight: '56px' }}
              >
                <Square className="w-5 h-5 mr-2" />
                CLOCK OUT
              </Button>
              <Button 
                type="button" 
                variant="ghost" 
                onClick={() => setShowEnhancedClockOut(false)}
                className="w-full sm:w-auto py-3 rounded-xl touch-manipulation"
              >
                Cancel
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {/* End of Week Modal */}
      {showEndOfWeek && weekSummary && (
        <Modal isOpen={showEndOfWeek} onClose={() => setShowEndOfWeek(false)} title="End of Week - Confirm Hours">
          <div className="space-y-6">
            <div className="p-4 bg-orange-50 rounded-lg border-2 border-orange-200">
              <p className="text-sm font-bold uppercase text-orange-600">Week of {weekSummary.week_start}</p>
              <div className="grid grid-cols-3 gap-4 mt-3">
                <div>
                  <p className="text-2xl font-bold font-mono text-orange-500">{weekSummary.total_hours}h</p>
                  <p className="text-xs text-slate-500">Hours This Week</p>
                </div>
                <div>
                  <p className="text-2xl font-bold font-mono">{weekSummary.entry_count}</p>
                  <p className="text-xs text-slate-500">Shifts</p>
                </div>
                <div>
                  <p className="text-2xl font-bold font-mono text-green-600">${weekSummary.total_reimbursement}</p>
                  <p className="text-xs text-slate-500">Reimbursements</p>
                </div>
              </div>
            </div>

            {/* Entries to Review/Adjust */}
            <div>
              <h4 className="font-bold uppercase mb-3">Review Your Hours</h4>
              <p className="text-sm text-slate-500 mb-4">You can adjust your hours below. Your supervisor will see both the original app-recorded times and any adjustments you make.</p>
              
              <div className="space-y-3 max-h-[300px] overflow-y-auto">
                {adjustedEntries.filter(e => e.clock_out).map((entry, idx) => (
                  <div key={entry.id} className="p-3 bg-slate-50 rounded-lg border">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold">{new Date(entry.clock_in).toLocaleDateString()}</span>
                      <span className="text-sm text-slate-500">{entry.job_title || 'No job'}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <div>
                        <label className="text-xs text-slate-500">Original Hours</label>
                        <p className="font-mono">{entry.total_hours}h</p>
                      </div>
                      <div>
                        <label className="text-xs text-slate-500">Adjust Hours</label>
                        <input
                          type="number"
                          step="0.5"
                          value={adjustedEntries[idx]?.adjusted_hours || entry.total_hours}
                          onChange={(e) => {
                            const updated = [...adjustedEntries];
                            updated[idx] = {...updated[idx], adjusted_hours: parseFloat(e.target.value)};
                            setAdjustedEntries(updated);
                          }}
                          className="w-full px-2 py-1 border rounded text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-500">Difference</label>
                        <p className={`font-mono ${(adjustedEntries[idx]?.adjusted_hours || entry.total_hours) !== entry.total_hours ? 'text-amber-600' : ''}`}>
                          {((adjustedEntries[idx]?.adjusted_hours || entry.total_hours) - entry.total_hours).toFixed(1)}h
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Supervisor Selection */}
            <Select
              label="Supervisor for Approval *"
              value={weekSummary.timesheet?.supervisor_id || clockOutData.supervisor_id}
              onChange={(e) => setClockOutData({...clockOutData, supervisor_id: e.target.value})}
              options={[
                { value: "", label: "-- Select Supervisor --" },
                ...weekSummary.supervisors.map(s => ({
                  value: s.id,
                  label: `${s.first_name} ${s.last_name} (${s.role})`
                }))
              ]}
            />

            <div className="flex gap-3 pt-4 border-t">
              <Button onClick={handleEndOfWeekSubmit} data-testid="submit-end-of-week">
                <CheckCircle className="w-5 h-5 mr-2" />
                Submit to Supervisor
              </Button>
              <Button variant="ghost" onClick={() => setShowEndOfWeek(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Document Signing Modal - Before Clock In */}
      {showMissedClockOutModal && missedClockOutInfo && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto" data-testid="missed-clockout-modal">
          <Card className="w-full max-w-md">
            <div className="flex items-center gap-3 mb-4 pb-4 border-b">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold uppercase">Missed Clock-Out</h2>
                <p className="text-xs text-slate-600">
                  Fix yesterday's hours before clocking in today
                </p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm">
              <p className="font-semibold text-amber-900">
                You clocked in on {new Date(missedClockOutInfo.clock_in_iso).toLocaleString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                {missedClockOutInfo.job_title ? ` for "${missedClockOutInfo.job_title}"` : ''}.
              </p>
              <p className="text-xs text-amber-700 mt-1">
                Open for {Math.floor(missedClockOutInfo.hours_open)}h {Math.round((missedClockOutInfo.hours_open % 1) * 60)}m. Enter what time you actually finished so your timesheet is accurate.
              </p>
            </div>

            <label className="text-xs font-bold uppercase text-slate-600 mb-1 block">Actual Clock-Out Time</label>
            <input
              type="datetime-local"
              className="w-full border-2 border-slate-200 rounded-lg p-3 mb-4"
              value={missedClockOutTime}
              min={missedClockOutInfo.clock_in_iso.slice(0, 16)}
              max={(() => { const d = new Date(); const pad = (n) => String(n).padStart(2,'0'); return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`; })()}
              onChange={(e) => setMissedClockOutTime(e.target.value)}
              data-testid="missed-clockout-time-input"
            />

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowMissedClockOutModal(false);
                  setMissedClockOutInfo(null);
                }}
                data-testid="missed-clockout-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  if (!missedClockOutTime) {
                    alert("Mate, pick a clock-out time first.");
                    return;
                  }
                  const outDt = new Date(missedClockOutTime);
                  const inDt = new Date(missedClockOutInfo.clock_in_iso);
                  if (outDt <= inDt) {
                    alert("Clock-out must be after the clock-in time.");
                    return;
                  }
                  setMissedClockOutSaving(true);
                  try {
                    await axios.post(`${API}/time/clock-out`, {
                      clock_out_time: outDt.toISOString(),
                      notes: 'Backdated: missed clock-out correction'
                    });
                    setShowMissedClockOutModal(false);
                    setMissedClockOutInfo(null);
                    await fetchData();
                    alert("Ripper — yesterday's shift closed off. Now clocking you in.");
                    // Retry the original clock-in
                    await handleClockAction('clock-in');
                  } catch (e) {
                    alert(e.response?.data?.detail || 'Failed to correct clock-out');
                  } finally {
                    setMissedClockOutSaving(false);
                  }
                }}
                disabled={missedClockOutSaving}
                data-testid="missed-clockout-submit"
              >
                {missedClockOutSaving ? <Spinner size="sm" /> : 'Correct & Continue'}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Document Signing Modal - Before Clock In */}
      {showSigningModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-3 mb-4 pb-4 border-b">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <FileText className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold uppercase">Documents Required</h2>
                <p className="text-sm text-slate-600">
                  Please sign the following document(s) before clocking in
                </p>
              </div>
            </div>

            {!currentSigningDoc ? (
              <div className="space-y-3">
                <p className="text-sm text-slate-500 mb-4">
                  You have {pendingSigningDocs.length} document(s) to sign:
                </p>
                {pendingSigningDocs.map((doc, idx) => (
                  <div key={doc.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border-2 border-slate-200">
                    <div>
                      <p className="font-bold">{doc.name}</p>
                      {doc.description && <p className="text-sm text-slate-500">{doc.description}</p>}
                    </div>
                    <Button onClick={() => setCurrentSigningDoc(doc)} size="sm">
                      Sign Now
                    </Button>
                  </div>
                ))}
                <div className="flex justify-end gap-2 pt-4 border-t mt-4">
                  <Button variant="outline" onClick={() => {
                    setShowSigningModal(false);
                    setPendingSigningDocs([]);
                  }}>
                    Cancel Clock In
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="font-bold text-lg mb-1">{currentSigningDoc.name}</h3>
                  {currentSigningDoc.description && (
                    <p className="text-sm text-slate-600">{currentSigningDoc.description}</p>
                  )}
                </div>
                
                {/* Use PDF Signer if document has placed signature fields, otherwise fallback to simple canvas */}
                {currentSigningDoc.signature_fields && currentSigningDoc.signature_fields.length > 0 ? (
                  <PDFDocumentSigner
                    fileUrl={currentSigningDoc.file_url}
                    signatureFields={currentSigningDoc.signature_fields}
                    userRole={user?.role}
                    onComplete={async (sigList) => {
                      try {
                        const jobIdForDoc = jobs.find(j => j.id === selectedJob)?.id;
                        // CRITICAL: use LOCAL date for daily instance keys, not UTC.
                        // new Date().toISOString().slice(0,10) returns UTC date — at
                        // 1am AEST that's still "yesterday" UTC, which made shifts /
                        // signatures land on the wrong day.
                        const _now = new Date();
                        const localToday = `${_now.getFullYear()}-${String(_now.getMonth()+1).padStart(2,'0')}-${String(_now.getDate()).padStart(2,'0')}`;
                        const instanceKey = currentSigningDoc.signing_frequency === 'daily' ? localToday : undefined;

                        // Split out external-kiosk signatures — those need the /external-signature endpoint per field
                        const externals = sigList.filter(s => s.is_external && !s.is_info_field);
                        const supervisorFills = sigList.filter(s => s.is_supervisor_fill);
                        const normal = sigList.filter(s => !s.is_external && !s.is_supervisor_fill);

                        // Supervisor filled text/date/checkbox values -> /fill-fields (one call with all fills)
                        if (supervisorFills.length > 0) {
                          await axios.post(`${API}/jobs/${jobIdForDoc}/signing-documents/${currentSigningDoc.id}/fill-fields`, {
                            filled_fields: supervisorFills.map(s => ({ field_id: s.field_id, value: s.signature_data, field_type: s.field_type })),
                            instance_key: instanceKey
                          });
                        }

                        // External kiosk signatures -> /external-signature per field
                        for (const ext of externals) {
                          await axios.post(`${API}/jobs/${jobIdForDoc}/signing-documents/${currentSigningDoc.id}/external-signature`, {
                            field_id: ext.field_id,
                            signature_data: ext.signature_data,
                            external_name: ext.external_name || 'External',
                            external_company: ext.external_company,
                            external_role: ext.external_role,
                            instance_key: instanceKey
                          });
                        }

                        // Current user's own signatures + info fields -> /sign
                        if (normal.length > 0) {
                          await axios.post(`${API}/jobs/${jobIdForDoc}/signing-documents/${currentSigningDoc.id}/sign`, {
                            signatures: normal,
                            instance_key: instanceKey
                          });
                        }

                        const remaining = pendingSigningDocs.filter(d => d.id !== currentSigningDoc.id);
                        setPendingSigningDocs(remaining);
                        setCurrentSigningDoc(null);
                        if (remaining.length === 0) {
                          setShowSigningModal(false);
                          toast.success("All documents signed! Clocking in...");
                          await proceedWithClockIn('clock-in');
                        } else {
                          toast.success("Document signed! " + remaining.length + " more to go.");
                        }
                      } catch (e) {
                        alert(e.response?.data?.detail || "Failed to sign document");
                      }
                    }}
                    onCancel={() => {
                      setCurrentSigningDoc(null);
                      setSigningSignature('');
                    }}
                  />
                ) : (
                  <>
                    {/* PDF View Link */}
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <span className="text-sm text-slate-600">Review the document before signing:</span>
                      <a 
                        href={currentSigningDoc.file_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-slate-200 text-slate-800 rounded-lg font-medium hover:bg-slate-300 transition-colors flex items-center gap-2"
                      >
                        <Eye className="w-4 h-4" /> View PDF
                      </a>
                    </div>
                    
                    {/* Simple Signature Pad */}
                    <div>
                      <label className="text-sm font-bold uppercase text-slate-600 mb-2 block">
                        Your Signature *
                      </label>
                      <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 bg-white">
                        <canvas
                          id="signing-canvas"
                          width={500}
                          height={150}
                          className="w-full border-2 border-slate-200 rounded cursor-crosshair bg-white"
                          style={{ touchAction: 'none' }}
                          onMouseDown={(e) => {
                            const canvas = document.getElementById('signing-canvas');
                            const ctx = canvas.getContext('2d');
                            ctx.lineWidth = 2;
                            ctx.lineCap = 'round';
                            ctx.strokeStyle = '#000';
                            ctx.beginPath();
                            const rect = canvas.getBoundingClientRect();
                            const scaleX = canvas.width / rect.width;
                            const scaleY = canvas.height / rect.height;
                            ctx.moveTo((e.clientX - rect.left) * scaleX, (e.clientY - rect.top) * scaleY);
                            canvas.isDrawing = true;
                          }}
                          onMouseMove={(e) => {
                            const canvas = document.getElementById('signing-canvas');
                            if (!canvas?.isDrawing) return;
                            const ctx = canvas.getContext('2d');
                            const rect = canvas.getBoundingClientRect();
                            const scaleX = canvas.width / rect.width;
                            const scaleY = canvas.height / rect.height;
                            ctx.lineTo((e.clientX - rect.left) * scaleX, (e.clientY - rect.top) * scaleY);
                            ctx.stroke();
                          }}
                          onMouseUp={() => {
                            const canvas = document.getElementById('signing-canvas');
                            if (canvas) {
                              canvas.isDrawing = false;
                              setSigningSignature(canvas.toDataURL());
                            }
                          }}
                          onMouseLeave={() => {
                            const canvas = document.getElementById('signing-canvas');
                            if (canvas) canvas.isDrawing = false;
                          }}
                          onTouchStart={(e) => {
                            e.preventDefault();
                            const canvas = document.getElementById('signing-canvas');
                            const ctx = canvas.getContext('2d');
                            ctx.lineWidth = 2;
                            ctx.lineCap = 'round';
                            ctx.strokeStyle = '#000';
                            ctx.beginPath();
                            const rect = canvas.getBoundingClientRect();
                            const touch = e.touches[0];
                            const scaleX = canvas.width / rect.width;
                            const scaleY = canvas.height / rect.height;
                            ctx.moveTo((touch.clientX - rect.left) * scaleX, (touch.clientY - rect.top) * scaleY);
                            canvas.isDrawing = true;
                          }}
                          onTouchMove={(e) => {
                            e.preventDefault();
                            const canvas = document.getElementById('signing-canvas');
                            if (!canvas?.isDrawing) return;
                            const ctx = canvas.getContext('2d');
                            const rect = canvas.getBoundingClientRect();
                            const touch = e.touches[0];
                            const scaleX = canvas.width / rect.width;
                            const scaleY = canvas.height / rect.height;
                            ctx.lineTo((touch.clientX - rect.left) * scaleX, (touch.clientY - rect.top) * scaleY);
                            ctx.stroke();
                          }}
                          onTouchEnd={() => {
                            const canvas = document.getElementById('signing-canvas');
                            if (canvas) {
                              canvas.isDrawing = false;
                              setSigningSignature(canvas.toDataURL());
                            }
                          }}
                        />
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="mt-2"
                          onClick={() => {
                            const canvas = document.getElementById('signing-canvas');
                            if (canvas) {
                              const ctx = canvas.getContext('2d');
                              ctx.clearRect(0, 0, canvas.width, canvas.height);
                              setSigningSignature('');
                            }
                          }}
                        >
                          Clear Signature
                        </Button>
                      </div>
                    </div>
                    
                    <div className="flex gap-2 pt-4 border-t">
                      <Button 
                        variant="outline" 
                        onClick={() => {
                          setCurrentSigningDoc(null);
                          setSigningSignature('');
                        }}
                      >
                        Back
                      </Button>
                      <Button 
                        className="flex-1"
                        onClick={async () => {
                          if (!signingSignature) {
                            alert("Please sign the document");
                            return;
                          }
                          try {
                            await axios.post(`${API}/jobs/${jobs.find(j => j.id === selectedJob)?.id}/signing-documents/${currentSigningDoc.id}/sign`, {
                              signatures: [{
                                field_id: 'main',
                                signature_data: signingSignature,
                                signed_at: new Date().toISOString()
                              }]
                            });
                            const remaining = pendingSigningDocs.filter(d => d.id !== currentSigningDoc.id);
                            setPendingSigningDocs(remaining);
                            setCurrentSigningDoc(null);
                            setSigningSignature('');
                            if (remaining.length === 0) {
                              setShowSigningModal(false);
                              toast.success("All documents signed! Clocking in...");
                              await proceedWithClockIn('clock-in');
                            } else {
                              toast.success("Document signed! " + remaining.length + " more to go.");
                            }
                          } catch (e) {
                            alert(e.response?.data?.detail || "Failed to sign document");
                          }
                        }}
                        disabled={!signingSignature}
                      >
                        Sign & Continue
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Summary Card */}
      <Card>
        <h3 className="text-xl font-bold uppercase tracking-tight font-heading mb-4">This Week</h3>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <p className="text-sm font-bold uppercase text-slate-500">Hours This Week</p>
            <p className="text-4xl font-bold font-mono text-orange-500">{summary?.total_hours || 0}</p>
          </div>
          <div>
            <p className="text-sm font-bold uppercase text-slate-500">Entries</p>
            <p className="text-4xl font-bold font-mono">{summary?.entry_count || 0}</p>
          </div>
        </div>
      </Card>

      {/* Working Away (LAA) — worker self-flag + dinner receipt upload */}
      <WorkingAwayCard />

      {/* On-shift receipts — visible only when clocked in */}
      <OnShiftReceiptCard
        clockedIn={!!clockStatus?.clocked_in}
        activeJob={clockStatus?.current_entry ? {
          job_id: clockStatus.current_entry.job_id,
          job_title: jobs.find(j => j.id === clockStatus.current_entry.job_id)?.title,
        } : null}
      />
        </div>
      )}

      {/* Travel Tab */}
      {activeTab === 'travel' && (
        <div className="space-y-6 pb-40 sm:pb-6">
          {/* Travel Clock In/Out Card */}
          <Card className="text-center" data-testid="travel-clock-card">
            <div className="mb-6">
              <p className="text-sm font-bold uppercase text-slate-500 mb-2">Travel Status</p>
              <Badge variant={travelStatus?.travelling ? "success" : "default"} className="text-lg px-6 py-2">
                {travelStatus?.travelling ? "Travelling" : "Not Travelling"}
              </Badge>
              {travelStatus?.entry && (
                <div className="mt-3 text-sm text-slate-600">
                  <Badge variant={travelStatus.entry.travel_role === "driver" ? "warning" : "info"} className="mb-2">
                    {travelStatus.entry.travel_role === "driver" ? "Driver" : "Passenger"}
                  </Badge>
                  <p><strong>From:</strong> {travelStatus.entry.starting_point}</p>
                  <p><strong>To:</strong> {travelStatus.entry.destination}</p>
                  <p><strong>Vehicle:</strong> {travelStatus.entry.vehicle_name || 'N/A'}</p>
                  <p><strong>Elapsed:</strong> {travelStatus.elapsed_hours?.toFixed(1)}h</p>
                  {travelStatus.entry.travel_role === "driver" && (
                    <p><strong>Fuel Stops:</strong> {travelStatus.fuel_stops_count}</p>
                  )}
                </div>
              )}
            </div>

            {travelStatus?.travelling ? (
              <div className="space-y-4 pb-48 sm:pb-0">
                {/* Fuel Stop Button - DRIVER ONLY (desktop only — mobile uses sticky version) */}
                {travelStatus?.entry?.travel_role === "driver" && (
                  <Button 
                    variant="outline" 
                    size="lg" 
                    onClick={() => setShowFuelStop(true)}
                    className="hidden sm:inline-flex w-full sm:w-auto"
                    data-testid="fuel-stop-btn"
                  >
                    <Fuel className="w-6 h-6 mr-2" />
                    Record Fuel Stop
                  </Button>
                )}

                {/* Travel Clock Out Form (inline) */}
                <Card className="text-left bg-slate-50">
                  <h4 className="font-bold uppercase mb-4 flex items-center gap-2">
                    <Square className="w-5 h-5 text-red-500" /> End Travel — Enter Details
                  </h4>
                  <form onSubmit={handleTravelClockOut} className="space-y-4" data-testid="travel-clock-out-form">
                    <Input
                      label="Finishing Point *"
                      value={travelClockOutData.finishing_point}
                      onChange={(e) => setTravelClockOutData({...travelClockOutData, finishing_point: e.target.value})}
                      placeholder="Where did you finish?"
                      required
                    />
                    {/* KM fields - DRIVER ONLY */}
                    {travelStatus?.entry?.travel_role === "driver" && (
                      <>
                        <Input
                          label="Ending KM (Odometer) *"
                          type="number"
                          step="0.1"
                          value={travelClockOutData.ending_km}
                          onChange={(e) => setTravelClockOutData({...travelClockOutData, ending_km: e.target.value})}
                          placeholder={travelStatus?.entry?.starting_km ? `Started at: ${travelStatus.entry.starting_km} km` : "Current odometer reading"}
                          required
                        />
                        {travelStatus?.entry?.starting_km && travelClockOutData.ending_km && (
                          <p className="text-sm text-green-600">
                            Distance: {(parseFloat(travelClockOutData.ending_km) - travelStatus.entry.starting_km).toFixed(1)} km
                          </p>
                        )}
                      </>
                    )}
                    <Textarea
                      label="Problems or Incidents During Travel"
                      value={travelClockOutData.incidents}
                      onChange={(e) => setTravelClockOutData({...travelClockOutData, incidents: e.target.value})}
                      placeholder="Any issues, accidents, breakdowns, or incidents?"
                    />
                    <Textarea
                      label="Notes"
                      value={travelClockOutData.notes}
                      onChange={(e) => setTravelClockOutData({...travelClockOutData, notes: e.target.value})}
                      placeholder="Any additional notes"
                    />
                    {/* Desktop submit button */}
                    <Button type="submit" className="hidden sm:inline-flex" data-testid="submit-travel-clock-out">
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Complete Travel
                    </Button>
                  </form>
                </Card>

                {/* MOBILE sticky End Travel button — sits above the bottom nav (h-20) so workers can tap without scrolling */}
                <div className="sm:hidden fixed bottom-20 left-0 right-0 z-50 p-3 bg-white border-t-2 border-orange-200 shadow-2xl" data-testid="mobile-end-travel-sticky">
                  <Button
                    type="button"
                    variant="destructive"
                    size="lg"
                    className="w-full h-14 text-base"
                    onClick={() => {
                      const form = document.querySelector('[data-testid="travel-clock-out-form"]');
                      if (form) form.requestSubmit();
                    }}
                    data-testid="mobile-end-travel-btn"
                  >
                    <Square className="w-6 h-6 mr-2" />
                    End Travel & Submit
                  </Button>
                </div>
              </div>
            ) : (
              <Button 
                variant="default" 
                size="lg" 
                onClick={() => setShowTravelClockIn(true)}
                data-testid="travel-clock-in-btn"
              >
                <Car className="w-6 h-6 mr-2" />
                Start Travel
              </Button>
            )}
          </Card>

          {/* Travel Clock In Modal */}
          {showTravelClockIn && (
            <Modal isOpen={showTravelClockIn} onClose={() => setShowTravelClockIn(false)} title="Travel Clock In">
              <form onSubmit={handleTravelClockIn} className="space-y-4">
                {/* Driver/Passenger Selection */}
                <div className="p-4 bg-slate-100 rounded-lg">
                  <h5 className="text-sm font-bold uppercase text-slate-600 mb-3">Are you driving or a passenger?</h5>
                  <div className="flex gap-4">
                    <button
                      type="button"
                      onClick={() => setTravelRole("driver")}
                      className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                        travelRole === "driver" 
                          ? "border-orange-500 bg-orange-50" 
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <Car className="w-8 h-8 mx-auto mb-2 text-orange-500" />
                      <p className="font-bold">Driver</p>
                      <p className="text-xs text-slate-500">I'm driving the vehicle</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setTravelRole("passenger")}
                      className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                        travelRole === "passenger" 
                          ? "border-blue-500 bg-blue-50" 
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <Users className="w-8 h-8 mx-auto mb-2 text-blue-500" />
                      <p className="font-bold">Passenger</p>
                      <p className="text-xs text-slate-500">I'm riding with someone</p>
                    </button>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <Input
                    label="Starting Point *"
                    value={travelClockInData.starting_point}
                    onChange={(e) => setTravelClockInData({...travelClockInData, starting_point: e.target.value})}
                    placeholder="e.g., Yard, Home, Job Site"
                    required
                  />
                  <Input
                    label="Destination *"
                    value={travelClockInData.destination}
                    onChange={(e) => setTravelClockInData({...travelClockInData, destination: e.target.value})}
                    placeholder="Where are you heading?"
                    required
                  />
                </div>

                {/* Passenger-specific: Join a Driver */}
                {travelRole === "passenger" && (
                  <div className="border-t pt-4 mt-4 bg-blue-50 p-4 rounded-lg">
                    <h5 className="text-sm font-bold uppercase text-blue-600 mb-3">Join a Driver</h5>
                    {activeDrivers.length > 0 ? (
                      <>
                        <Select
                          label="Select Driver to Join"
                          value={travelClockInData.join_travel_id}
                          onChange={(e) => {
                            const driver = activeDrivers.find(d => d.id === e.target.value);
                            setTravelClockInData({
                              ...travelClockInData, 
                              join_travel_id: e.target.value,
                              driver_name: driver?.user_name || ""
                            });
                          }}
                          options={[
                            { value: "", label: "-- Select Active Driver --" },
                            ...activeDrivers.map(d => ({
                              value: d.id,
                              label: `${d.user_name} - ${d.vehicle_name || 'Unknown'} (${d.destination})`
                            }))
                          ]}
                        />
                        <p className="text-xs text-slate-500 mt-2">Or enter driver name manually below if not listed</p>
                      </>
                    ) : (
                      <p className="text-sm text-slate-600">No active drivers found. Enter driver name below.</p>
                    )}
                    <Input
                      label="Driver's Name"
                      value={travelClockInData.driver_name}
                      onChange={(e) => setTravelClockInData({...travelClockInData, driver_name: e.target.value})}
                      placeholder="Who is driving?"
                      className="mt-3"
                    />
                  </div>
                )}

                {/* Driver-specific: Vehicle Details */}
                {travelRole === "driver" && (
                  <>
                    <div className="border-t pt-4 mt-4">
                      <h5 className="text-sm font-bold uppercase text-slate-600 mb-3">Vehicle Details</h5>
                      <Select
                        label="Select Vehicle from Fleet"
                        value={travelClockInData.vehicle_id}
                        onChange={(e) => {
                          const machine = machines.find(m => m.id === e.target.value);
                          setTravelClockInData({
                            ...travelClockInData, 
                            vehicle_id: e.target.value,
                            vehicle_name: machine?.name || "",
                            vehicle_rego: machine?.registration || ""
                          });
                        }}
                        options={[
                          { value: "", label: "-- Select or enter manually below --" },
                          ...machines.filter(m => m.machine_type?.toLowerCase().includes('vehicle') || m.machine_type?.toLowerCase().includes('truck') || m.machine_type?.toLowerCase().includes('ute')).map(m => ({
                            value: m.id,
                            label: `${m.name} (${m.registration || 'No rego'})`
                          })),
                          ...machines.filter(m => !m.machine_type?.toLowerCase().includes('vehicle') && !m.machine_type?.toLowerCase().includes('truck') && !m.machine_type?.toLowerCase().includes('ute')).map(m => ({
                            value: m.id,
                            label: `${m.name} - ${m.machine_type || 'Machine'}`
                          }))
                        ]}
                      />
                      <div className="grid md:grid-cols-2 gap-4 mt-3">
                        <Input
                          label="Vehicle Name"
                          value={travelClockInData.vehicle_name}
                          onChange={(e) => setTravelClockInData({...travelClockInData, vehicle_name: e.target.value})}
                          placeholder="e.g., White Hilux"
                        />
                        <Input
                          label="Rego"
                          value={travelClockInData.vehicle_rego}
                          onChange={(e) => setTravelClockInData({...travelClockInData, vehicle_rego: e.target.value})}
                          placeholder="e.g., ABC123"
                        />
                      </div>
                    </div>

                    <div className="border-t pt-4 mt-4">
                      <h5 className="text-sm font-bold uppercase text-slate-600 mb-3">Odometer & Checks</h5>
                      <Input
                        label="Starting KM (Odometer) *"
                        type="number"
                        step="0.1"
                        value={travelClockInData.starting_km}
                        onChange={(e) => setTravelClockInData({...travelClockInData, starting_km: e.target.value})}
                        placeholder="Current odometer reading"
                        required
                      />
                      <div className="flex items-center gap-3 mt-4 p-3 bg-amber-50 rounded-lg border-2 border-amber-200">
                        <input
                          type="checkbox"
                          id="preStartChecked"
                          checked={travelClockInData.pre_start_checked}
                          onChange={(e) => setTravelClockInData({...travelClockInData, pre_start_checked: e.target.checked})}
                          className="w-5 h-5 rounded border-2 border-amber-400 text-orange-500 focus:ring-orange-500"
                        />
                        <label htmlFor="preStartChecked" className="font-semibold text-amber-800">
                          Pre-start check completed & fluids checked before operation
                        </label>
                      </div>
                    </div>

                    <div className="border-t pt-4 mt-4">
                      <h5 className="text-sm font-bold uppercase text-slate-600 mb-3">Faults & Notes</h5>
                      <Textarea
                        label="Problems/Faults on Vehicle"
                        value={travelClockInData.machine_faults}
                        onChange={(e) => setTravelClockInData({...travelClockInData, machine_faults: e.target.value})}
                        placeholder="Any existing damage, faults, or issues with the vehicle?"
                      />
                    </div>
                  </>
                )}

                <Textarea
                  label="Notes"
                  value={travelClockInData.notes}
                  onChange={(e) => setTravelClockInData({...travelClockInData, notes: e.target.value})}
                  placeholder="Any additional notes"
                />

                <div className="flex gap-3 pt-4 border-t">
                  <Button type="submit" data-testid="submit-travel-clock-in">
                    <Play className="w-5 h-5 mr-2" />
                    Start Travel as {travelRole === "driver" ? "Driver" : "Passenger"}
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => setShowTravelClockIn(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            </Modal>
          )}

          {/* Fuel Stop Modal */}
          {showFuelStop && (
            <Modal isOpen={showFuelStop} onClose={() => setShowFuelStop(false)} title="Record Fuel Stop">
              <form onSubmit={handleFuelStop} className="space-y-4">
                <Input
                  label="Current KM (Odometer) *"
                  type="number"
                  step="0.1"
                  value={fuelStopData.km_at_stop}
                  onChange={(e) => setFuelStopData({...fuelStopData, km_at_stop: e.target.value})}
                  placeholder="Odometer reading at fuel station"
                  required
                />
                <Input
                  label="Station Location *"
                  value={fuelStopData.station_location}
                  onChange={(e) => setFuelStopData({...fuelStopData, station_location: e.target.value})}
                  placeholder="e.g., Shell Penrith, BP Blacktown"
                  required
                />
                <div className="grid md:grid-cols-2 gap-4">
                  <Input
                    label="Fuel Amount (Litres)"
                    type="number"
                    step="0.1"
                    value={fuelStopData.fuel_amount}
                    onChange={(e) => setFuelStopData({...fuelStopData, fuel_amount: e.target.value})}
                    placeholder="e.g., 65.5"
                  />
                  <Input
                    label="Total Cost ($)"
                    type="number"
                    step="0.01"
                    value={fuelStopData.fuel_cost}
                    onChange={(e) => setFuelStopData({...fuelStopData, fuel_cost: e.target.value})}
                    placeholder="e.g., 125.50"
                  />
                </div>

                <Select
                  label="Payment Type *"
                  value={fuelStopData.payment_type}
                  onChange={(e) => setFuelStopData({...fuelStopData, payment_type: e.target.value})}
                  options={[
                    { value: "company", label: "Company Card / Paid" },
                    { value: "reimbursement", label: "Personal - Need Reimbursement" }
                  ]}
                />

                <div>
                  <label className="block text-sm font-bold uppercase text-slate-600 mb-2">
                    Upload Fuel Receipt
                  </label>
                  {fuelStopData.receipt_url ? (
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-sm text-green-700">Receipt uploaded</span>
                      <Button 
                        type="button"
                        variant="ghost" 
                        size="sm" 
                        onClick={() => setFuelStopData({...fuelStopData, receipt_url: null})}
                      >
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleReceiptUpload(e.target.files[0])}
                      className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
                    />
                  )}
                </div>

                <Textarea
                  label="Notes"
                  value={fuelStopData.notes}
                  onChange={(e) => setFuelStopData({...fuelStopData, notes: e.target.value})}
                  placeholder="Any notes about this fuel stop"
                />

                <div className="flex gap-3 pt-4 border-t">
                  <Button type="submit" data-testid="submit-fuel-stop">
                    <Fuel className="w-5 h-5 mr-2" />
                    Record Fuel Stop
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => setShowFuelStop(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            </Modal>
          )}

          {/* Travel History */}
          <Card data-testid="travel-history">
            <h3 className="text-xl font-bold uppercase tracking-tight font-heading mb-4">Travel History</h3>
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {travelEntries.map(entry => (
                <div 
                  key={entry.id} 
                  className={`p-4 rounded-lg border-2 ${
                    entry.status === 'travelling' ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Car className="w-5 h-5 text-orange-500" />
                        <span className="font-bold">{entry.starting_point} → {entry.finishing_point || entry.destination}</span>
                        <Badge variant={entry.status === 'travelling' ? 'success' : 'default'}>
                          {entry.status === 'travelling' ? 'Active' : 'Complete'}
                        </Badge>
                      </div>
                      <div className="text-sm text-slate-600 space-y-1">
                        <p><strong>Vehicle:</strong> {entry.vehicle_name || 'N/A'} {entry.vehicle_rego ? `(${entry.vehicle_rego})` : ''}</p>
                        <p><strong>Date:</strong> {new Date(entry.clock_in).toLocaleDateString()}</p>
                        <p><strong>KM:</strong> {entry.starting_km} → {entry.ending_km || '...'} 
                          {entry.total_km && <span className="text-green-600 ml-2">({entry.total_km} km total)</span>}
                        </p>
                        {entry.fuel_stops?.length > 0 && (
                          <p><strong>Fuel Stops:</strong> {entry.fuel_stops.length} (${entry.total_fuel_cost?.toFixed(2) || '0.00'})</p>
                        )}
                        {entry.incidents && (
                          <p className="text-red-600"><strong>Incidents:</strong> {entry.incidents}</p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold font-mono text-orange-500">
                        {entry.total_hours?.toFixed(1) || '...'}h
                      </p>
                      {entry.reimbursement_needed > 0 && (
                        <Badge variant="warning" className="mt-1">
                          ${entry.reimbursement_needed.toFixed(2)} reimb.
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {travelEntries.length === 0 && (
                <p className="text-center text-slate-500 py-8">No travel entries yet</p>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* All Entries Tab - Admin/Manager only */}
      {activeTab === 'all-entries' && canViewReports && (
        <Card>
          <h3 className="text-lg font-bold uppercase mb-4">All Time Entries</h3>
          <div className="space-y-3 max-h-[600px] overflow-y-auto">
            {allEntries.map(entry => (
              <div key={entry.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border-2 border-slate-100">
                <div className="flex-1">
                  <p className="font-semibold">{entry.user_name || 'Unknown'}</p>
                  <p className="text-sm text-slate-600">{entry.job_title || 'No job'}</p>
                  <p className="text-xs text-slate-400">
                    {new Date(entry.clock_in).toLocaleString()} - {entry.clock_out ? new Date(entry.clock_out).toLocaleString() : 'Active'}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-lg font-bold font-mono text-orange-500">
                    {entry.total_hours ? `${entry.total_hours}h` : 'Active'}
                  </span>
                  {!entry.clock_out && (
                    <Button size="sm" variant="outline" onClick={() => handleForceClockOutEntry(entry.id)}>
                      <Square className="w-4 h-4" />
                    </Button>
                  )}
                  {isAdmin && (
                    <Button size="sm" variant="ghost" onClick={() => handleCancelEntry(entry.id)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Worker Reports Tab */}
      {activeTab === 'reports' && canViewReports && (
        <div className="space-y-6">
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold uppercase">Worker Time Reports</h3>
              <Select
                value={reportPeriod}
                onChange={(e) => setReportPeriod(e.target.value)}
                options={[
                  { value: "4weeks", label: "Last 4 Weeks" },
                  { value: "3months", label: "Last 3 Months" },
                  { value: "6months", label: "Last 6 Months" },
                  { value: "12months", label: "Last 12 Months" }
                ]}
              />
            </div>

            {workerReports?.workers?.length > 0 ? (
              <div className="space-y-4">
                {workerReports.workers.map(worker => (
                  <div key={worker.user_id} className="border-2 border-slate-200 rounded-lg overflow-hidden">
                    <div className="flex items-center justify-between p-4 bg-slate-50">
                      <div>
                        <p className="font-bold text-lg">{worker.user_name}</p>
                        <p className="text-sm text-slate-500">{worker.total_entries} entries in period</p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-orange-500">{worker.total_hours?.toFixed(1) || 0}h</p>
                        <p className="text-xs text-slate-400">in period</p>
                      </div>
                    </div>
                    {/* Weekly breakdown */}
                    {(() => {
                      const weeks = {};
                      (worker.entries || []).forEach(e => {
                        if (!e.clock_in) return;
                        const d = new Date(e.clock_in);
                        const day = d.getDay();
                        const mon = new Date(d);
                        mon.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
                        const key = mon.toISOString().split('T')[0];
                        if (!weeks[key]) weeks[key] = { hours: 0, entries: 0 };
                        weeks[key].hours += e.total_hours || 0;
                        weeks[key].entries += 1;
                      });
                      const sortedWeeks = Object.entries(weeks).sort((a, b) => b[0].localeCompare(a[0]));
                      return sortedWeeks.length > 0 ? (
                        <div className="px-4 pt-2 pb-3">
                          <p className="text-xs font-bold uppercase text-slate-400 mb-2">Weekly Breakdown</p>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            {sortedWeeks.slice(0, 8).map(([wk, data]) => (
                              <div key={wk} className="text-center p-2 bg-slate-50 rounded-lg border">
                                <p className="text-xs text-slate-500">w/c {new Date(wk).toLocaleDateString('en-AU', { day: '2-digit', month: 'short' })}</p>
                                <p className="text-lg font-bold text-orange-600">{data.hours.toFixed(1)}h</p>
                                <p className="text-[10px] text-slate-400">{data.entries} entries</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null;
                    })()}
                    <div className="p-4 max-h-48 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-slate-500 border-b">
                            <th className="pb-2">Date</th>
                            <th className="pb-2">Job</th>
                            <th className="pb-2">In</th>
                            <th className="pb-2">Out</th>
                            <th className="pb-2 text-right">Hours</th>
                            {isAdmin && <th className="pb-2"></th>}
                          </tr>
                        </thead>
                        <tbody>
                          {worker.entries.slice(0, 20).map(entry => (
                            <tr key={entry.id || entry.clock_in} className="border-b border-slate-100">
                              <td className="py-2">{new Date(entry.clock_in).toLocaleDateString()}</td>
                              <td className="py-2">{entry.job_title || '-'}</td>
                              <td className="py-2">{new Date(entry.clock_in).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</td>
                              <td className="py-2">{entry.clock_out ? new Date(entry.clock_out).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : 'Active'}</td>
                              <td className="py-2 text-right font-mono">{entry.total_hours || '-'}</td>
                              {isAdmin && (
                                <td className="py-2 text-right">
                                  <button onClick={() => handleCancelEntry(entry.id)} className="text-red-500 hover:text-red-700">
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-slate-500 py-8">No data for selected period</p>
            )}
          </Card>
        </div>
      )}
    </div>
  );
};

export default TimePage;
